import React, { useContext, useEffect, useState } from "react";
import { Row, Col, Button, Typography, Skeleton, Empty } from "antd";
import { useNavigate } from "react-router-dom";
import btnStyles from "../../../styles/Custom/Button.module.css";
import DietForm from "./DietForm";
import { PlusOutlined, CloudUploadOutlined } from "@ant-design/icons";
import { getDietApi, uploadDietApi } from "../../../services/diet.services";
import BulkUploadModal from "../../common/BulkUploadModal";
import { prepareDietBulkUploadFile } from "../../../helpers/bulkUploadExcel";
import DietTableEdit from "./DietTableEdit";
import CustomLayout from "../../Layout/CustomLayout";
import SideMenu from "../../Layout/CustomLayout/SideMenu";
import typoStyles from "../../../styles/Custom/Typography.module.css";
import { UserContext } from "../../../context/UserContext";
import { getCookie } from "../../../helpers/localStorage";
import { Role } from "../../../constants/defaultKeys";
import { UiDataTable } from "../../ui";
import useQuery from "../../../hooks/useQuery";
import {
  updateUrlSearchParams,
  parsePageParam,
  parseSearchParam,
} from "../../../helpers/urlFilters";

const DietMenu = () => {
  const navigate = useNavigate();
  const params = useQuery();
  const { userDetails } = useContext(UserContext);
  const [isVisible, setIsVisible] = useState(false);
  const [uploadModalOpen, setUploadModalOpen] = useState(false);
  const [dietData, setDietData] = useState([]);
  const [refresh, setRefresh] = useState(false);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(() => parsePageParam(params));
  const [searchQuery, setSearchQuery] = useState(() => parseSearchParam(params));
  const refreshPage = () => {
    setRefresh((prev) => !prev);
  };

  useEffect(() => {
    let queryParams = { page: page, limit: 100 };
    if (searchQuery) {
      queryParams.search = searchQuery;
      queryParams.page = 1;
    }
    const queryString = new URLSearchParams(queryParams);
    setLoading(true);
    getDietApi(queryString)
      .then((res) => {
        setDietData(res?.data);
        setLoading(false);
      })
      .catch((err) => {
        console.log(err);
        setLoading(false);
      });
  }, [refresh, page, searchQuery]);
  const defaultRole = userDetails?.currRole || getCookie(Role);
  const onDietSearch = (value) => {
    const query = value?.target?.value;
    setSearchQuery(query || null);
    setPage(1);
    updateUrlSearchParams(navigate, { search: query || null, page: null });
  };

  const handlePageChange = (value) => {
    setPage(value);
    updateUrlSearchParams(navigate, { page: value === 1 ? null : value });
  };
  return (
    <CustomLayout
      header={<Typography className={typoStyles.h6}>Diet Menu</Typography>}
      sideContent={<SideMenu />}
    >
      <>
        {dietData?.length < 0 ? (
          <Row className="facilities">
            {defaultRole === "Facility_Admin" ? (
              <Empty />
            ) : (
              <Col span={24} style={{ textAlign: "center" }}>
                <Button
                  type="primary"
                  onClick={() => setIsVisible(true)}
                  className={btnStyles.lgBtn}
                >
                  <PlusOutlined /> Diet Menu
                </Button>
              </Col>
            )}
          </Row>
        ) : (
          <UiDataTable
            showHeader={false}
            shellClassName="ui-table-fill-shell"
            onSearch={onDietSearch}
            searchPlaceholder="Search diet"
            toolbarTrailing={
              defaultRole !== "Facility_Admin" ? (
                <div style={{ display: "flex", gap: 12 }}>
                  <Button
                    icon={<CloudUploadOutlined />}
                    onClick={() => setUploadModalOpen(true)}
                    className={btnStyles.lgBtn}
                  >
                    Upload Excel
                  </Button>
                  <Button
                    type="primary"
                    onClick={() => setIsVisible(true)}
                    className={btnStyles.lgBtn}
                  >
                    <PlusOutlined /> Menu Item
                  </Button>
                </div>
              ) : null
            }
          >
            <DietTableEdit
              data={dietData}
              setData={setDietData}
              refreshPage={refreshPage}
              loading={loading}
              page={page}
              setPage={handlePageChange}
              userDetails={userDetails}
            />
          </UiDataTable>
        )}
      </>

      {isVisible && (
        <DietForm
          open={isVisible}
          setOpen={setIsVisible}
          refreshPage={refreshPage}
        />
      )}

      {uploadModalOpen && (
        <BulkUploadModal
          open={uploadModalOpen}
          setOpen={setUploadModalOpen}
          refreshPage={refreshPage}
          title="Bulk Upload Diet Items"
          requiredColumns={[
            "Name",
            "Protien",
            "Calories",
            "Sodium",
            "Potassium",
            "Carbohydrates",
          ]}
          sampleFileName="bulk_import_curengo_dietitem_sample.xlsx"
          uploadApi={uploadDietApi}
          fileField="dietitems"
          prepareUploadFile={prepareDietBulkUploadFile}
          sampleHint="Row 2 is a grey reference example only and is not uploaded."
          sampleDownloadHint="Download the sample file. Row 2 shows an example in grey; add your diet items from row 3 onward."
        />
      )}
    </CustomLayout>
  );
};

export default DietMenu;
